//src/pages/ResgatesManuais.jsx
import React, { useState } from "react";
import axios from "../axiosConfig";
import {
  AlertTriangle,
  CheckCircle2,
  Eye,
  Gift,
  Search,
  ShieldCheck,
  UserRoundCheck,
} from "lucide-react";

import "./ResgatesManuais.css";

const ResgatesManuais = () => {
  const [termoEmpresa, setTermoEmpresa] = useState("");
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);

  const [premioId, setPremioId] = useState("");
  const [quantidade, setQuantidade] = useState(1);
  const [dataResgate, setDataResgate] = useState("");
  const [tipoEnvio, setTipoEnvio] = useState("PAC");
  const [destinoEnvio, setDestinoEnvio] = useState("");
  const [codigoRastreio, setCodigoRastreio] = useState("");
  const [observacoes, setObservacoes] = useState("");

  const [mensagem, setMensagem] = useState("");
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mostrarResumo, setMostrarResumo] = useState(false);
  const [dadosResumo, setDadosResumo] = useState(null);

  const buscarUsuarios = async () => {
    if (!termoEmpresa.trim()) {
      setMensagem("Digite parte da razão social ou CNPJ da empresa.");
      return;
    }

    try {
      setLoadingBusca(true);
      setMensagem("");

      const res = await axios.get("/admin/usuarios-por-empresa", {
        params: { termo: termoEmpresa },
      });

      setUsuarios(Array.isArray(res.data) ? res.data : []);

      if (Array.isArray(res.data) && res.data.length === 0) {
        setMensagem("Nenhum usuário encontrado para esta empresa.");
      }
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      setMensagem("Erro ao buscar usuários da empresa.");
    } finally {
      setLoadingBusca(false);
    }
  };

  const handleSelecionarUsuario = (u) => {
    setUsuarioSelecionado(u);

    const destinoDefault = `${u.nome} - ${u.nome_empresa || ""} - ${
      u.cidade || ""
    }/${u.estado || ""}`;

    setDestinoEnvio((prev) => prev || destinoDefault);
  };

  const dataResgateFormatada =
    dataResgate &&
    (() => {
      try {
        const d = new Date(dataResgate);
        return d.toLocaleString("pt-BR");
      } catch {
        return dataResgate;
      }
    })();

  const handleGerarResumo = async () => {
    if (!usuarioSelecionado) {
      setMensagem("Selecione o usuário que receberá o resgate.");
      setMostrarResumo(false);
      return;
    }

    if (!premioId) {
      setMensagem("Informe o ID do prêmio.");
      setMostrarResumo(false);
      return;
    }

    if (!dataResgate) {
      setMensagem("Informe a data do resgate (retroativa ou atual).");
      setMostrarResumo(false);
      return;
    }

    try {
      setMensagem("");
      setSalvando(true);
      setMostrarResumo(false);

      const payload = {
        usuario_id: usuarioSelecionado.id,
        premio_id: Number(premioId),
        quantidade: Number(quantidade) || 1,
        data_resgate: dataResgate,
        tipo_envio: tipoEnvio,
        destino_envio: destinoEnvio,
        codigo_rastreio: codigoRastreio,
        observacoes,
      };

      const res = await axios.post("/admin/resgates/manual/preview", payload);

      setDadosResumo(res.data);
      setMostrarResumo(true);
    } catch (err) {
      console.error("Erro ao gerar preview de resgate manual:", err);

      const msgApi =
        err.response?.data?.error || "Erro ao gerar resumo do resgate.";

      setMensagem(msgApi);
      setMostrarResumo(false);
    } finally {
      setSalvando(false);
    }
  };

  const handleConfirmarResgate = async () => {
    if (!usuarioSelecionado) return;

    try {
      setSalvando(true);
      setMensagem("");

      const payload = {
        usuario_id: usuarioSelecionado.id,
        premio_id: Number(premioId),
        quantidade: Number(quantidade) || 1,
        data_resgate: dataResgate,
        tipo_envio: tipoEnvio,
        destino_envio: destinoEnvio,
        codigo_rastreio: codigoRastreio,
        observacoes,
      };

      const res = await axios.post("/admin/resgates/manual", payload);

      setMensagem(
        `Resgate manual criado! ID: ${res.data.resgateId} | Pontos debitados: ${res.data.pontos_debitados}`
      );

      setMostrarResumo(false);
      setDadosResumo(null);
    } catch (err) {
      console.error("Erro ao criar resgate manual:", err);

      const msgApi =
        err.response?.data?.error || "Erro ao criar resgate manual.";

      setMensagem(msgApi);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="resgatesManuaisAdmin-page">
      <div className="resgatesManuaisAdmin-topbar">
        <div>
          <span className="resgatesManuaisAdmin-kicker">
            Correção de histórico
          </span>

          <h2>Resgates manuais</h2>

          <p>
            Registre resgates retroativos ou correções aprovadas manualmente,
            com preview antes da gravação definitiva.
          </p>
        </div>
      </div>

      {mensagem && (
        <div className="resgatesManuaisAdmin-alerta">
          <AlertTriangle size={16} />
          <span>{mensagem}</span>
        </div>
      )}

      <div className="resgatesManuaisAdmin-card">
        <div className="resgatesManuaisAdmin-cardHeader">
          <div className="resgatesManuaisAdmin-icon">
            <UserRoundCheck size={20} />
          </div>

          <div>
            <h3>Selecionar usuário pela imobiliária</h3>
            <span>
              Busque por parte da razão social ou CNPJ e escolha o cliente que
              receberá o resgate.
            </span>
          </div>
        </div>

        <div className="resgatesManuaisAdmin-busca">
          <div className="resgatesManuaisAdmin-searchInput">
            <Search size={15} />

            <input
              type="text"
              placeholder="Digite parte da razão social ou CNPJ da empresa"
              value={termoEmpresa}
              onChange={(e) => setTermoEmpresa(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="resgatesManuaisAdmin-btn primary"
            onClick={buscarUsuarios}
            disabled={loadingBusca}
          >
            <Search size={16} />
            {loadingBusca ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {usuarios.length > 0 && (
          <div className="resgatesManuaisAdmin-tableWrapper compact">
            <table className="resgatesManuaisAdmin-table">
              <thead>
                <tr>
                  <th>Sel.</th>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Empresa</th>
                  <th>Cidade/UF</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {usuarios.map((u) => (
                  <tr
                    key={u.id}
                    className={
                      usuarioSelecionado?.id === u.id ? "selecionado" : ""
                    }
                  >
                    <td>
                      <input
                        type="radio"
                        name="usuarioSelecionado"
                        checked={usuarioSelecionado?.id === u.id}
                        onChange={() => handleSelecionarUsuario(u)}
                      />
                    </td>

                    <td>
                      <strong>{u.nome}</strong>
                    </td>

                    <td>{u.email}</td>

                    <td>{u.nome_empresa}</td>

                    <td>
                      {u.cidade} / {u.estado}
                    </td>

                    <td>
                      <span className="resgatesManuaisAdmin-status">
                        {u.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="resgatesManuaisAdmin-card">
        <div className="resgatesManuaisAdmin-cardHeader">
          <div className="resgatesManuaisAdmin-icon teal">
            <Gift size={20} />
          </div>

          <div>
            <h3>Dados do resgate</h3>
            <span>
              Informe o prêmio, quantidade, data retroativa e dados de envio.
            </span>
          </div>
        </div>

        <div className="resgatesManuaisAdmin-grid">
          <div className="resgatesManuaisAdmin-field">
            <label>ID do prêmio</label>

            <input
              type="number"
              value={premioId}
              onChange={(e) => setPremioId(e.target.value)}
              placeholder="Ex: 123"
            />

            <small>Código do produto no cadastro de prêmios.</small>
          </div>

          <div className="resgatesManuaisAdmin-field">
            <label>Quantidade</label>

            <input
              type="number"
              min={1}
              value={quantidade}
              onChange={(e) => setQuantidade(e.target.value)}
            />
          </div>

          <div className="resgatesManuaisAdmin-field">
            <label>Data do resgate</label>

            <input
              type="datetime-local"
              value={dataResgate}
              onChange={(e) => setDataResgate(e.target.value)}
            />
          </div>

          <div className="resgatesManuaisAdmin-field">
            <label>Tipo de envio</label>

            <select
              value={tipoEnvio}
              onChange={(e) => setTipoEnvio(e.target.value)}
            >
              <option value="PAC">PAC</option>
              <option value="SEDEX">SEDEX</option>
            </select>
          </div>

          <div className="resgatesManuaisAdmin-field">
            <label>Código de rastreio</label>

            <input
              type="text"
              value={codigoRastreio}
              onChange={(e) => setCodigoRastreio(e.target.value)}
              placeholder="Opcional"
            />
          </div>

          <div className="resgatesManuaisAdmin-field full">
            <label>Destino de envio</label>

            <textarea
              rows={2}
              value={destinoEnvio}
              onChange={(e) => setDestinoEnvio(e.target.value)}
              placeholder="Endereço / observações do destino"
            />
          </div>

          <div className="resgatesManuaisAdmin-field full">
            <label>Observações internas</label>

            <textarea
              rows={3}
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              placeholder="Observações internas sobre o lançamento manual"
            />
          </div>
        </div>

        {mostrarResumo && usuarioSelecionado && dadosResumo && (
          <div className="resgatesManuaisAdmin-resumo">
            <div className="resgatesManuaisAdmin-resumoHeader">
              <Eye size={18} />
              <span>Resumo do resgate antes de confirmar</span>
            </div>

            <ul>
              <li>
                <strong>Cliente:</strong> {usuarioSelecionado.nome} (
                {usuarioSelecionado.email})
              </li>

              <li>
                <strong>Empresa:</strong> {usuarioSelecionado.nome_empresa} —{" "}
                {usuarioSelecionado.cidade}/{usuarioSelecionado.estado}
              </li>

              <li>
                <strong>Prêmio:</strong> {dadosResumo.premio_nome} (ID{" "}
                {dadosResumo.premio_id})
              </li>

              <li>
                <strong>Quantidade:</strong> {dadosResumo.quantidade}
              </li>

              <li>
                <strong>Data do resgate:</strong>{" "}
                {dataResgateFormatada || dataResgate}
              </li>

              <li>
                <strong>Pontos por unidade:</strong>{" "}
                {dadosResumo.pontos_por_unidade} pts (produto{" "}
                {dadosResumo.pontos_produto} + frete{" "}
                {dadosResumo.pontos_frete})
              </li>

              <li>
                <strong>Total de pontos a debitar:</strong>{" "}
                {dadosResumo.pontos_totais} pts
              </li>

              <li>
                <strong>Tipo de envio:</strong> {tipoEnvio}
              </li>

              <li>
                <strong>Destino:</strong> {destinoEnvio || "—"}
              </li>

              <li>
                <strong>Código de rastreio:</strong> {codigoRastreio || "—"}
              </li>

              {observacoes && (
                <li>
                  <strong>Observações:</strong> {observacoes}
                </li>
              )}
            </ul>

            <p className="resgatesManuaisAdmin-resumoAlerta">
              <ShieldCheck size={15} />
              Este lançamento será gravado como resgate manual aprovado e
              enviado, debitando {dadosResumo.pontos_totais} pontos do cliente e
              impactando o histórico.
            </p>
          </div>
        )}

        <div className="resgatesManuaisAdmin-actions">
          {mostrarResumo ? (
            <>
              <button
                type="button"
                className="resgatesManuaisAdmin-btn secondary"
                onClick={() => {
                  setMostrarResumo(false);
                  setDadosResumo(null);
                }}
                disabled={salvando}
              >
                Voltar e editar
              </button>

              <button
                type="button"
                className="resgatesManuaisAdmin-btn success"
                onClick={handleConfirmarResgate}
                disabled={salvando}
              >
                <CheckCircle2 size={18} />
                {salvando ? "Salvando..." : "Confirmar resgate manual"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="resgatesManuaisAdmin-btn primary"
              onClick={handleGerarResumo}
              disabled={salvando}
            >
              <Eye size={18} />
              Ver resumo antes de salvar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResgatesManuais;